/**
 * Approve the prepared quotation for the order system, under the "Prepare quotation"
 * preview.
 *
 * "Approve for the order system" records the rep's approval in the app database (with
 * an idempotency key and the frozen quotation). It is enabled only when every check of
 * the preview passed. Creating quotations in the order system is switched off: nothing
 * reads the approval and nothing is sent, which the panel says every time, with the
 * seven closed gates. The approver, or a manager who may reopen cases of others, can
 * withdraw it. Earlier approvals are listed below.
 *
 * Managers who may approve prices see "Approve price" on the lines whose price needs an
 * approval. No price, cost or margin figure is shown or sent for that.
 */

import { Check, Icon, Undo2 } from "@norican/icons";
import { Alert, Badge, Button, Input, Typography } from "@norican/react";
import { useState } from "react";

import {
  APPROVAL_SWITCHED_OFF,
  approvalFailure,
  approvalHistory,
  type FoDraftLine,
  type FoDraftPreview,
  type FoGate,
  mayWithdraw,
  personName,
  type QuotationApproval,
  useApproveQuotation,
  useLinePriceApproval,
  useMe,
  useQuotationApproval,
  useWithdrawQuotation,
} from "@/lib/api";
import { hasCapability } from "@/lib/areas";
import { logger } from "@/lib/logger";
import { PRICE_APPROVAL_WORDS } from "@/lib/sales-flow";
import { formatDayTime } from "@/lib/work-labels";

const STATE_LABELS: Record<string, string> = {
  approved: "Approved",
  withdrawn: "Withdrawn",
  superseded: "Replaced by a newer approval",
};

/** "Approved by Anna Jensen, 08 Oct 2026, 14:05." */
export function approvedSentence(approval: Pick<QuotationApproval, "approved_by" | "approved_at">) {
  const who = personName(approval.approved_by) || "a colleague";
  const when = formatDayTime(approval.approved_at);
  return `Approved by ${who}${when ? `, ${when}` : ""}.`;
}

function GateList({ gates }: { gates: FoGate[] }) {
  const usable = gates.filter((gate) => gate && typeof gate.code === "string");
  if (usable.length === 0) {
    return null;
  }
  return (
    <ul className="flex flex-col gap-1">
      {usable.map((gate) => (
        <li key={gate.code} className="flex items-start gap-2">
          <Badge tone="neutral" variant="outline" size="xs" className="mt-0.5 shrink-0">
            {gate.code} closed
          </Badge>
          <span className="text-fg-muted min-w-0 text-xs break-words">{gate.label}</span>
        </li>
      ))}
    </ul>
  );
}

/** Approve price on the lines that need it. Managers with `approve_price` only. */
function PriceApprovals({
  caseId,
  lines,
  lineIndexOf,
}: {
  caseId: string;
  lines: FoDraftLine[];
  lineIndexOf: (lineKey: string) => number | undefined;
}) {
  const approve = useLinePriceApproval(caseId);
  const needing = lines.filter(
    (line) =>
      line.margin_status === "needs_approval" &&
      typeof line.line_key === "string" &&
      lineIndexOf(line.line_key) !== undefined,
  );
  if (needing.length === 0) {
    return null;
  }
  const failure = approve.error ? approvalFailure(approve.error) : null;
  // The approval is read from the preview line (`price_approval`), never kept here: the
  // write reads the preview again, so the line says "approved" also after the sheet was
  // closed and opened (ease round, 13 Oct 2026: the page forgot an approval).
  const send = (lineKey: string, value: boolean) => {
    const lineIndex = lineIndexOf(lineKey);
    if (lineIndex === undefined) {
      return;
    }
    logger.debug("[case] price approval", { case: caseId, lineIndex, approve: value });
    approve.mutate({ lineIndex, approve: value });
  };
  return (
    <div className="flex flex-col gap-1.5">
      <Typography variant="caption" tone="subtle">
        Prices that need your approval
      </Typography>
      <ul className="flex flex-col gap-1.5">
        {needing.map((line) => {
          const key = line.line_key as string;
          // An approval that no longer matches the line ("stale") reads as none.
          const approved = line.price_approval === "approved";
          return (
            <li key={key} className="flex flex-wrap items-center gap-2">
              <span className="text-fg min-w-0 flex-1 font-mono text-xs font-bold break-all">
                {line.item}
                {line.item_name && (
                  <span className="text-fg-muted font-sans font-normal"> {line.item_name}</span>
                )}
              </span>
              {approved ? (
                <>
                  <span className="text-fg-muted text-xs" data-price-approved={key}>
                    Price approved by {personName(line.price_approved_by) || "a manager"}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    tone="neutral"
                    disabled={approve.isPending}
                    onClick={() => send(key, false)}
                  >
                    {PRICE_APPROVAL_WORDS.takeBack}
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  tone="accent"
                  disabled={approve.isPending}
                  startIcon={<Icon icon={Check} size="xs" />}
                  onClick={() => send(key, true)}
                >
                  Approve price
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {failure && failure.kind !== "conflict" && (
        <Typography variant="caption" className="text-danger-fg" role="alert">
          {failure.kind === "forbidden" ? "Your role does not allow this." : failure.message}
        </Typography>
      )}
    </div>
  );
}

export interface QuotationApprovalSectionProps {
  caseId: string;
  preview: FoDraftPreview;
  /** The position of a line in the stored draft by its key; undefined when not known. */
  lineIndexOf?: (lineKey: string) => number | undefined;
}

/** The approval under the preview. */
export function QuotationApprovalSection({
  caseId,
  preview,
  lineIndexOf = () => undefined,
}: QuotationApprovalSectionProps) {
  const { data: me } = useMe();
  const state = useQuotationApproval(caseId);
  const approve = useApproveQuotation(caseId);
  const withdraw = useWithdrawQuotation(caseId);
  const [note, setNote] = useState("");

  const current = state.data?.current ?? null;
  const approved = current?.state === "approved" ? current : null;
  const history = approvalHistory(state.data);
  const ready = preview.ready === true;
  const reopenOthers = hasCapability(me, "reopen_others");
  const canWithdraw = mayWithdraw(approved, me?.email, reopenOthers);
  const approveFailure = approve.error ? approvalFailure(approve.error) : null;
  const withdrawFailure = withdraw.error ? approvalFailure(withdraw.error) : null;
  const lines = Array.isArray(preview.lines) ? preview.lines : [];

  return (
    <div className="border-border-subtle flex flex-col gap-3 border-t pt-3">
      {approved ? (
        <Alert tone="success" title="Approved for the order system">
          <span className="flex flex-col gap-1">
            <span>
              {approvedSentence(approved)} {APPROVAL_SWITCHED_OFF}
            </span>
            {approved.reference && (
              <span className="font-mono text-xs break-all">{approved.reference}</span>
            )}
          </span>
        </Alert>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            tone="accent"
            disabled={!ready || approve.isPending || state.isLoading}
            loading={approve.isPending}
            startIcon={<Icon icon={Check} size="xs" />}
            onClick={() => {
              logger.debug("[case] approve quotation", { case: caseId });
              approve.mutate();
            }}
          >
            Approve for the order system
          </Button>
          <Typography variant="caption" tone="muted" className="min-w-0 break-words">
            {ready
              ? "Records your approval in the workbench only. Writing quotations to F&O is switched off."
              : "Possible once every check above passed."}
          </Typography>
        </div>
      )}

      {approveFailure && approveFailure.kind !== "conflict" && (
        <Alert tone="warning" title="Not approved">
          <span className="flex flex-col gap-1">
            <span>{approveFailure.message}</span>
            {approveFailure.kind === "blocked" && approveFailure.checks.length > 0 && (
              <ul className="flex list-disc flex-col gap-0.5 pl-5">
                {approveFailure.checks.map((check) => (
                  <li key={check.code ?? check.label} className="break-words">
                    {check.label}
                    {check.detail ? `: ${check.detail}` : ""}
                  </li>
                ))}
              </ul>
            )}
          </span>
        </Alert>
      )}

      {approved && canWithdraw && (
        <div className="flex flex-wrap items-center gap-2">
          <Input
            size="sm"
            value={note}
            maxLength={300}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Why (optional)"
            aria-label="Why you withdraw the approval"
            className="max-w-72"
          />
          <Button
            size="sm"
            variant="outline"
            tone="neutral"
            loading={withdraw.isPending}
            startIcon={<Icon icon={Undo2} size="xs" />}
            onClick={() => {
              logger.debug("[case] withdraw quotation approval", { case: caseId });
              withdraw.mutate({ note }, { onSuccess: () => setNote("") });
            }}
          >
            Withdraw
          </Button>
        </div>
      )}
      {withdrawFailure && withdrawFailure.kind !== "conflict" && (
        <Typography variant="caption" className="text-danger-fg" role="alert">
          {withdrawFailure.message}
        </Typography>
      )}

      {approved && <GateList gates={Array.isArray(approved.gates) ? approved.gates : []} />}

      {hasCapability(me, "approve_price") && (
        <PriceApprovals caseId={caseId} lines={lines} lineIndexOf={lineIndexOf} />
      )}

      {history.length > 0 && (
        <div className="flex flex-col gap-1">
          <Typography variant="caption" tone="subtle">
            Earlier approvals
          </Typography>
          <ul className="flex flex-col gap-1">
            {history.map((entry) => (
              <li key={entry.intent_id} className="text-fg-muted text-xs break-words">
                {STATE_LABELS[entry.state] ?? entry.state}: {approvedSentence(entry)}
                {entry.withdrawn_at
                  ? ` Withdrawn by ${personName(entry.withdrawn_by) || "a colleague"}, ${formatDayTime(entry.withdrawn_at)}.`
                  : ""}
                {entry.note ? ` Note: ${entry.note}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
